// src/careReferral/components/payInvoiceButtons.tsx
import React from "react";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";

type Props = {
  amount: number;                 // dollars
  leadId?: string | number;       // optional, for tracing
  seed: string;                   // stable per invoice (e.g., invoice.id)
  onCreated?: (orderId: string) => void;
  onApproved?: (captureId: string) => void;
  onError?: (err: any) => void;
};

export default function PayInvoiceButtons({
  amount,
  leadId,
  seed,
  onCreated,
  onApproved,
  onError,
}: Props) {
  const [{ isResolved }] = usePayPalScriptReducer();


  const amountStr = React.useMemo(() => Number(amount || 0).toFixed(2), [amount]);

  // Unique invoice_id per attempt (prevents DUPLICATE_INVOICE_ID)
  const invoiceId = React.useMemo(
    () => `${seed}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    [seed, amountStr]
  );

  // Stable base id for tracing (shows up as custom_id)
  const customId = React.useMemo(
    () => (leadId != null ? String(leadId) : seed),
    [leadId, seed]
  );
  // Guard: don’t render buttons until SDK is ready
  if (!isResolved) return null;

  return (
    <PayPalButtons
      style={{ layout: "vertical" }}
      // Only re-render the *buttons* when these change; does not reload the SDK
      forceReRender={[amountStr, customId]}

      createOrder={(_, actions) =>
        actions.order
          .create({
            intent: "CAPTURE",
            purchase_units: [
              {
                invoice_id: invoiceId,                 // must be unique
                custom_id: customId,                   // your internal link
                amount: { currency_code: "USD", value: amountStr },
              },
            ],
          })
          .then((orderId) => {
            onCreated?.(orderId);
            return orderId;
          })
      }

      onApprove={async (_data, actions) => {
        const details = await actions.order?.capture();
        const captureId =
          details?.purchase_units?.[0]?.payments?.captures?.[0]?.id ||
          details?.id ||
          "PAYPAL_CAPTURE";
        onApproved?.(captureId);
      }}

      onError={(err) => {
        // This also catches the SDK’s console errors; lets you surface a toast
        console.error("PayPal error:", err);
        onError?.(err);
      }}
    />
  );
}
